// Angular imports
import { Injectable } from '@angular/core';

// Fast xml parser
import { XMLParser } from 'fast-xml-parser';

// Import services
import { ApplicationDataService } from './application-data.service';
import { MessageService, MessageType } from './message.service';

// Import types
import { CalculationHeaderApi, CalculationLineApi, CalculationMetaApi } from '../types';
import { AppState } from '../state-management/app-state';
import { Store } from '@ngrx/store';
import * as CalculationActions from '../state-management/calculation-actions';
import { BehaviorSubject } from 'rxjs';
import { selectCalculationMeta, selectHourRates } from '../state-management/calculation-selectors';

// Define constants
export const IMPORTABLE_CUF_FILE_EXTENSIONS = ['.xml', '.cuf'];

@Injectable({
    providedIn: 'root'
})
export class ImportService {
    private meta$ = new BehaviorSubject<CalculationMetaApi>(undefined);
    private hourRates$ = new BehaviorSubject<CalculationHeaderApi[]>(undefined);

    constructor(
        private store: Store<AppState>,
        private messageService: MessageService
    ) {
        this.store.select(selectCalculationMeta).subscribe(
            meta => this.meta$.next(meta)
        );
        this.store.select(selectHourRates).subscribe(
            rates => this.hourRates$.next(rates)
        );
    }

    public async importCuf(cufXml: string): Promise<void> {
        // Get the current project and calculation
        const project = this.meta$.value.projectId;
        const calculation = this.meta$.value.id;

        // Ensure both project and calculation are loaded
        if (!project || !calculation) throw new Error('Calculation not loaded when importing');

        // Parse cuf file
        const { CUF: cuf } = this.parseXML(cufXml);

        // Ensure cuf was properly parsed
        if (!cuf) throw new InvalidCufError();

        // Parse cuf to line parts
        const linesParts = Object.values((cuf['BEGROTING']['BEGROTINGSREGEL'] as any[]).reduce((lines, value) => {
            // Get the code
            const [code] = value.attributes['CODE'].split('.');

            // Ensure line has entry
            if (!(code in lines))
                lines[code] = [];

            // Push to line entry
            lines[code].push(value);

            return lines;
        }, {} as { [code: string]: any[] })).map((lineParts: any[], index) => {
            // Get the types
            lineParts = lineParts.map(value => {
                // Get the type
                const [_, type] = value.attributes['CODE'].split('.');
                return { type, ...value }
            });

            return lineParts;
        });

        // Get all unique hourly rates that don't yet exist in the calculation
        const newRates = linesParts
            .map(lineParts => lineParts.find(p => p.type === 'A')?.attributes['UUR_TARIEF'])
            .filter(v => !!v)
            .map(v => Number(v))
            .filter((v, i, self) => self.indexOf(v) === i)
            .filter(v => !this.hourRates$.value.find(hr => hr.value === v))

        // Create all new hourly rates
        newRates.forEach((rate, i) => this.store.dispatch(CalculationActions.insertHeaderLine({
            projectId: this.meta$.value.projectId,
            calculationId: this.meta$.value.id,
            line: {
                id: null, 
                sourceVersion: 0,
                description: `Uurloon ${rate}`,
                sortOrder: (this.hourRates$.value.length + 1 + i) * 100,
                value: rate
            }
        })));
        
        // addHourlyRate(project, calculation, `Uurloon ${rate}`, (calculation.hourlyRates.length + 1 + i) * 100, rate));

        // Add new hourly rates to calculation
        // calculation.hourlyRates.push(...newHourlyRates);

        // Parse line parts to lines
        const lines = linesParts.map<CalculationLineApi>(lineParts => {
            // Get general line info
            const [code] = (lineParts[0].attributes['CODE'] as string).split('.');
            const description = lineParts[0].attributes['OMSCHRIJVING'];
            const unit = lineParts[0].attributes['HOEVEELHEID_EENHEID'].toLowerCase();
            const quantity = lineParts[0].attributes['HOEVEELHEID'];

            // Get the correct lines for each type
            const labor = lineParts.find(p => p.type === 'A');
            const material = lineParts.find(p => p.type === 'MA');
            const equipment = lineParts.find(p => p.type === 'ME');
            const subcontracting = lineParts.find(p => p.type === 'OA');

            // Build line
            return {
                id: null,
                code: `${code.slice(0, 2)}.${code.slice(2)}`,
                description,
                unit,
                quantity,
                sourceVersion: 0,
                sortOrder: 0,
                hours: { hourlyRate: labor ? this.hourRates$.value.find(hr => hr.value === Number(labor.attributes['UUR_TARIEF']))! : this.hourRates$.value[0] },
                material: material ? { norm: Number(material.attributes['MATERIAALPRIJS']) } : undefined,
                equipment: equipment ? { norm: Number(equipment.attributes['MATERIEELPRIJS']) } : undefined,
                subcontracting: subcontracting ? { norm: Number(subcontracting.attributes['ONDERAANNEMINGSPRIJS']) } : undefined,
            } as CalculationLineApi
        });

        // Bulk add lines
        this.store.dispatch(CalculationActions.insertLines({
            projectId: this.meta$.value.projectId,
            calculationId: this.meta$.value.id,
            lines
        }))
        // this.dataService.bulkAddLines(project, calculation, lines);

        // Add the lines into the calculation
        // calculation.insert(...lines);

        // If new hourly rates were created, show a message to the user indicating so
        if (newRates.length) {
            this.messageService.show({
                type: MessageType.Warning,
                title: 'Let op',
                message: `Tijdens het importeren zijn de volgende uurlonen aangemaakt: ${newRates.map(r => `Uurloon ${r}`).join(' ,')}.`
            });
        }
    }

    private parseXML(xml: string): any {
        try {
            const parser = new XMLParser();
            return parser.parse(xml);
        } catch (err) {
            throw new InvalidXmlError(err.message);
        }
    }
}

export enum ImportErrorType {
    InvalidXML,
    InvalidCUF
}

export abstract class ImportError extends Error {
    public readonly type: ImportErrorType;

    constructor(type: ImportErrorType, message?: string) {
        super(message);

        this.type = type;
    }
}

export class InvalidXmlError extends ImportError {
    constructor(message?: string) {
        super(ImportErrorType.InvalidXML, message);
    }
}

export class InvalidCufError extends ImportError {
    constructor(message?: string) {
        super(ImportErrorType.InvalidCUF, message);
    }
}